import Grid from '@material-ui/core/Grid';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import { APPLICATION_PATHS, ENTITLEMENTS } from '../../application-core/constants';
import ActionText from '../../core/ActionText';
import BasicReadField from '../../core/BasicReadField';
import EditedIriLink from '../../core/EditedIriLink';
import ExternalResourceLink from '../../core/ExternalResourceLink';
import Notification from '../../core/Notification';
import TitledPanel from '../../core/TitledPanel';
import Warning from '../../core/Warning';
import { ProjectConsumer } from '../../core/providers/ProjectProvider';
import { globalStyles as styles } from '../../core/style/gstheme';
import { STATUS_ACCEPTED, STATUS_REJECTED } from '../../core/utils/constants';
import { checkResourceEntitlements } from '../../core/utils/utils';
import withGS from '../../core/withGShoc';
import WorkflowResourceManager from '../../core/workflow/WorkflowResourceManager';
import GS from '../../grafsync/grafsync';
import SchemeChip from '../core/SchemeChip';
import AnnotationField from '../taxonomies/AnnotationField';
import AssociatedProjectDisplay from '../../application-core/AssociatedProjectDisplay';
import LabelRelationshipsDisplay from './LabelRelationshipsDisplay';
import TypeDisplay from '../taxonomies/TypeDisplay';
import FindRelatedLabelPassthroughDialog from './FindRelatedLabelPassthroughDialog';
import ReificationContainer from '../../application-core/ReificationContainer';

const APPROVED = STATUS_ACCEPTED;
const REJECTED = STATUS_REJECTED;

class LabelDetail extends React.Component {

    state = {
        warning: "",
        reificationSubject: undefined,
        reificationPredicate: undefined,
        reificationObject: undefined
    };

    onUpdate = (property, lang) => (value) => this.props.onUpdate(property, value, lang);

    onActualUpdate = (property, lang, index, propertyGraphIRI) => (value) =>
        this.props.onUpdate(property, value, lang, index, propertyGraphIRI);

    // requiredDomain is used to check whether the resources still has the domain at the point of adding
    // Certainly unlikely that somebody removes it in the interim but we check regardless
    // And if they do remove it this user's screen will rerender so they would have had to hit the add button just at the right time!
    addNewTypedLiteral = (property, propertyGraphIRI, requiredDomains = []) => (value, datatype = null) => {
        let isValidForDomain = true;
        if (requiredDomains && requiredDomains.length > 0) {
            isValidForDomain = requiredDomains.some(domainClass => {
                const dataTypes = Array.isArray(this.props.data.type) ? this.props.data.type : [this.props.data.type];
                if (dataTypes.includes(domainClass)) return true;
                const alias = GS.getAlias(domainClass);
                if (dataTypes.includes(alias)) return true;
                return false;
            });
        }
        if (!isValidForDomain) {
            this.setState({ warning: "Somebody has removed the class necessary to allow adding this property." });
        }
        else GS.addProperty(this.props.id, this.props.graph, property, value, null, datatype, propertyGraphIRI);
    };

    addNewLiteral = (property, propertyGraphIRI) => (language, text) => {
        GS.addProperty(this.props.id, this.props.graph, property, text, language, null, propertyGraphIRI);
    };

    includeExistingResource = (property, propertyGraphIRI) => (newIRI) => {
        // We load the concept we are about to use to make sure rendering works okay
        // But if it's external it won't load and that's okay
        GS.loadData([newIRI], {}, this.props.graph).then(() => {
            GS.addProperty(this.props.id, this.props.graph, property, newIRI, null, null, propertyGraphIRI);
        });
    };

    onDelete = (property, lang, datatype, propertyGraphIRI) => (value) =>
        this.props.onDelete(property, value, lang, datatype, propertyGraphIRI);

    onAddClass = async (value) => this.props.onAdd("type", value);

    onExitWarning = () => this.setState({ warning: "" });

    onShowReification = (showReification, reificationSubject, reificationPredicate, reificationObject) => {
        this.setState({
            showReification,
            reificationSubject,
            reificationPredicate,
            reificationObject
        });
    };

    render() {

        const { data, schemeIRI, graph, viewLang, id, showOntologyProjects, useWorkflow, projectUsers, entitlements,
            propertyTemplates, associatedProject, linkedData, annotate, useSlashIRI } = this.props;
        const { showReification, reificationSubject, reificationPredicate, reificationObject } = this.state;
        const { literalForm } = data;

        if (data._notAvailable) return null;

        if (this.state.warning !== "") return <Warning message={this.state.warning} onExitWarning={this.onExitWarning} />;

        // When viewing in floating view there is no scheme so data is not editable
        const isReadOnly = !schemeIRI || checkResourceEntitlements(data, ENTITLEMENTS.EDIT_TAXONOMY)
            || !data.inScheme || !data.inScheme.includes(schemeIRI)
            || (useWorkflow && "status" in data && (data.status[0] === APPROVED || data.status[0] === REJECTED));

        if (data.pendingReplacement) return <ActionText>This label has been replaced. Select again to view new data.</ActionText>;

        const lang = Object.keys(literalForm)[0];
        const isXlLabelOf = data.isXlPrefLabelOf || data.isXlAltLabelOf || data.isXlHiddenLabelOf || null;
        const links = Array.isArray(isXlLabelOf) ? isXlLabelOf : [isXlLabelOf];
        const xlLabelType = data.isXlPrefLabelOf ?
            "preferred"
            : data.isXlAltLabelOf ?
                "alternative"
                : data.isXlHiddenLabelOf ?
                    "hidden"
                    :
                    null;
        const isXlLabelOfName = (data.isXlPrefLabelOf && "Preferred") || (data.isXlAltLabelOf && "Alternative") || (data.isXlHiddenLabelOf && "Hidden") || null;
        let resourceClasses = Array.isArray(data.type) ? data.type : [data.type];
        // Some of the classes may be aliased so convert to IRIs
        resourceClasses = resourceClasses.map(rc => GS.getIRIfromAlias(rc) || rc);

        return (
            <div>
                <ProjectConsumer>
                    {({ id: projectIRI, projectLang, defaultLang, hasComponent, ontologyProjectAssociation }) => (
                        <form aria-label="Label detail" autoComplete="off">
                            <Grid container spacing={1}>
                                {useWorkflow &&
                                    <Grid item xs={12}>
                                        <WorkflowResourceManager
                                            disabled={(!data.inScheme || data.inScheme.length === 0)
                                                || !(data.inScheme.includes(schemeIRI))
                                                || data.deprecated || data.pendingDeletion}
                                            status={data.status}
                                            id={id}
                                            key={id}
                                            resourceClass="http://www.w3.org/2008/05/skos-xl#Label"
                                            componentIRI={schemeIRI}
                                            defaultLang={defaultLang}
                                            entitlements={entitlements}
                                            assignedUser={data.assignedUser}
                                            assignedNote={data.assignedNote}
                                            rejectedNote={data.rejectedNote}
                                            projectUsers={projectUsers}
                                            project={projectIRI}
                                            graph={graph} />
                                    </Grid>
                                }
                                <Grid item xs={12}>
                                    {data.pendingDeletion &&
                                        <Notification error={true} text="This label is pending deletion." dataTest="pending-deletion" />
                                    }
                                    {(!data.inScheme || data.inScheme.length === 0) &&
                                        <Notification text="This label is not in any scheme." dataTest="not-in-scheme" />
                                    }
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TitledPanel title="Literal Form">
                                        <AnnotationField
                                            dataTest="literal-form"
                                            ariaLabel="Literal form"
                                            checkValues={true}
                                            schemeIRI={schemeIRI}
                                            conceptIRI={Array.isArray(isXlLabelOf) ? isXlLabelOf[0] : isXlLabelOf}
                                            xlLabelType={xlLabelType}
                                            multiline={true}
                                            projectIRI={projectIRI}
                                            canDeleteIfDefault={false}
                                            onUpdate={this.onUpdate("literalForm", lang)}
                                            defaultLang={lang}
                                            isReadOnly={isReadOnly}
                                            property="literalForm"
                                            text={literalForm[lang]}
                                            lang={lang} />
                                    </TitledPanel>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TypeDisplay
                                        textRef="ADD_CLASS"
                                        graph={graph}
                                        additionalEBP={
                                            ["http://www.w3.org/2004/02/skos/core#Concept"]
                                        }
                                        classIRIs={GS.getClassIRIs(id, graph)}
                                        ontologyProjectAssociation={ontologyProjectAssociation}
                                        isReadOnly={isReadOnly}
                                        defaultLang={defaultLang}
                                        viewLang={viewLang}
                                        onAddClass={this.onAddClass}
                                        id={id}
                                        values={data.type} />
                                </Grid>
                                {isXlLabelOf &&
                                    <Grid item xs={12} md={6}>
                                        <TitledPanel dataTest="isLabelOf" title={"Is " + isXlLabelOfName + " Label Of"}>
                                            {links.sort().map(lo => <EditedIriLink
                                                key={lo}
                                                dataTest="is-of"
                                                id={lo}
                                                handleNotAvailable={true}
                                                graph={graph}
                                                link={
                                                    data.inScheme && data.inScheme.length > 0 && data.inScheme.includes(this.props.schemeIRI) ?
                                                        APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/taxonomy#project='
                                                        + projectIRI + "&view=concept&taxonomy="
                                                        + this.props.schemeIRI
                                                        + "&concept=" + lo
                                                        :
                                                        null
                                                } />
                                            )}
                                        </TitledPanel>
                                    </Grid>
                                }
                                <Grid item xs={12} md={6}>
                                    <LabelRelationshipsDisplay
                                        labelIRI={data.id}
                                        graph={graph}
                                        useWorkflow={useWorkflow}
                                        projectIRI={projectIRI}
                                        defaultLang={defaultLang}
                                        components={hasComponent}
                                        schemeIRI={schemeIRI}
                                        values={data.xlLabelRelation ? data.xlLabelRelation : []}
                                        isReadOnly={isReadOnly}
                                        includeExistingLabel={this.includeExistingResource("xlLabelRelation")} />

                                </Grid>
                                {data.inScheme &&
                                    <Grid item xs={12} md={6}>
                                        <TitledPanel dataTest="inScheme" height="full" title="In Scheme">
                                            {data.inScheme.map(sIRI => hasComponent.includes(sIRI) ?
                                                <SchemeChip
                                                    key={sIRI}
                                                    id={sIRI}
                                                    iri={sIRI}
                                                    isReadOnly={isReadOnly}
                                                    graph={graph}
                                                    viewLang={viewLang}
                                                    defaultLang={defaultLang}
                                                    source={data.id}
                                                    property="inScheme"
                                                    link={
                                                        APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/taxonomy#project='
                                                        + projectIRI + "&taxonomy=" + sIRI + "&view=label&label=" + data.id
                                                    } />
                                                :
                                                <ExternalResourceLink
                                                    key={sIRI}
                                                    id={sIRI}
                                                    graph={graph}
                                                    isReadOnly={isReadOnly}
                                                    property="inScheme" />
                                            )}
                                        </TitledPanel>
                                    </Grid>
                                }
                                {data.iri &&
                                    <Grid item xs={12} lg={6}>
                                        <TitledPanel dataTest="iri-history" title="IRI History">
                                            {data.iri.reverse().map((iri, index) =>
                                                index > 0 ?
                                                    <BasicReadField key={index} dataTest="iri-history-item" margin="dense" text={iri} fullWidth />
                                                    :
                                                    null
                                            )}
                                            <BasicReadField dataTest="iri-history-item" margin="dense" text={id} fullWidth />
                                        </TitledPanel>
                                    </Grid>
                                }
                                {showOntologyProjects && ontologyProjectAssociation &&
                                    [...ontologyProjectAssociation].sort().map((opa) =>
                                        <AssociatedProjectDisplay
                                            resourceData={data}
                                            reify={annotate}
                                            onReify={this.onShowReification}
                                            includeBaseClass={true}
                                            baseInferredClass="http://www.w3.org/2008/05/skos-xl#Label"
                                            dialogComponent={FindRelatedLabelPassthroughDialog}
                                            projectIRI={projectIRI}
                                            projectType="TaxonomyProject"
                                            schemeIRI={schemeIRI}
                                            statements={data.isSubjectOfStatement}
                                            useWorkflow={useWorkflow}
                                            graph={graph}
                                            isReadOnly={isReadOnly}
                                            viewLang={viewLang}
                                            linkedData={linkedData}
                                            associatedTaxonomyProject={associatedProject}
                                            propertyTemplates={propertyTemplates}
                                            projectLang={projectLang}
                                            addNewLiteral={this.addNewLiteral}
                                            addNewTypedLiteral={this.addNewTypedLiteral}
                                            includeExistingResource={this.includeExistingResource}
                                            components={hasComponent}
                                            resourceClasses={resourceClasses}
                                            onDelete={this.onDelete}
                                            onUpdate={this.onActualUpdate}
                                            key={opa}
                                            id={opa}
                                            defaultLang={defaultLang} />
                                    )
                                }
                            </Grid>
                            {showReification &&
                                <ReificationContainer
                                    resourceData={data}
                                    subject={reificationSubject}
                                    dialogComponent={FindRelatedLabelPassthroughDialog}
                                    property={reificationPredicate}
                                    value={reificationObject}
                                    useSlashIRI={useSlashIRI}
                                    statements={data.isSubjectOfStatement}
                                    ontologyProjectAssociation={ontologyProjectAssociation}
                                    graph={graph}
                                    isReadOnly={isReadOnly}
                                    showDialog={this.onShowReification}
                                    projectIRI={projectIRI}
                                    baseIRI={schemeIRI}
                                    componentIRI={schemeIRI}
                                    useWorkflow={useWorkflow}
                                    propertyTemplates={propertyTemplates}
                                    viewLang={viewLang}
                                    projectLang={projectLang}
                                    projectType="TaxonomyProject"
                                    linkedData={linkedData}
                                    associatedTaxonomyProject={associatedProject}
                                    components={hasComponent}
                                    defaultLang={defaultLang} />}
                        </form>
                    )}
                </ProjectConsumer>
                <div data-test="info-panel-bottom" >&#160;</div>
            </div>
        );
    }
}

LabelDetail.defaulProps = {
    useWorkflow: false
};

LabelDetail.propTypes = {
    classes: PropTypes.object,
    schemeIRI: PropTypes.string,
    viewLang: PropTypes.string,
    useWorkflow: PropTypes.bool,
    projectUsers: PropTypes.array,
    id: PropTypes.string.isRequired,
    entitlements: PropTypes.array.isRequired,
    graph: PropTypes.string.isRequired,
    useSlashIRI: PropTypes.bool,
    propertyTemplates: PropTypes.array,
    linkedData: PropTypes.array,
    associatedProject: PropTypes.array,
    showOntologyProjects: PropTypes.bool.isRequired,
    onUpdate: PropTypes.func,
    onDelete: PropTypes.func,
    data: PropTypes.object.isRequired,
};

export default withStyles(styles)(withGS(LabelDetail, ["ALL"], null, {}, true));
